import React, { useState, useEffect } from 'react';
import TakeInventoryModal from './TakeInventoryModal';
import TeamInventorySection from './TeamInventorySection';
import LineChart from './LineChart';
import BarChart from './BarChart';
import InfoIcon from '../../assets/info.svg';
import TrashIcon from '../../assets/trash.svg';
import PenIcon from '../../assets/pen.svg';
import './InventoryPage.css';

function InventoryPage({ user, inModal = false }) {
  const [currentSnapshot, setCurrentSnapshot] = useState(null);
  const [allSnapshots, setAllSnapshots] = useState([]);
  const [growth, setGrowth] = useState(null);
  const [monthlyGrowth, setMonthlyGrowth] = useState(null);
  const [teamInventory, setTeamInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTakeModal, setShowTakeModal] = useState(false);
  const [editingSnapshot, setEditingSnapshot] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [viewMode, setViewMode] = useState('personal'); // 'personal' or 'member'
  const [selectedMetric, setSelectedMetric] = useState('infinity'); // 'infinity', 'monthly', 'community'
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch user's inventory data
      const statsResponse = await fetch('/backend/api/inventory/get-stats.php', {
        credentials: 'include'
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.success) {
          setCurrentSnapshot(statsData.current);
          setAllSnapshots(statsData.all_snapshots || []);
          setGrowth(statsData.growth_indicators);
          setMonthlyGrowth(statsData.monthly_growth);
        }
      }

      // Fetch team inventory data (only if in personal view)
      if (viewMode === 'personal') {
        const teamResponse = await fetch('/backend/api/inventory/team-inventory.php', {
          credentials: 'include'
        });
        
        if (teamResponse.ok) {
          const teamData = await teamResponse.json();
          if (teamData.success) {
            setTeamInventory(teamData.team_inventory);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberData = async (memberId) => {
    try {
      setLoading(true);
      const response = await fetch(`/backend/api/inventory/team-inventory.php?member_id=${memberId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCurrentSnapshot(data.stats.current);
          setAllSnapshots(data.stats.all_snapshots || []);
          setGrowth(data.stats.growth_indicators);
          setMonthlyGrowth(data.stats.monthly_growth);
          setSelectedMember(data.member);
        }
      }
    } catch (error) {
      console.error('Error fetching member data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (member) => {
    setViewMode('member');
    setSelectedMember(member);
    fetchMemberData(member.id);
  };

  const handleBackToPersonal = () => {
    setViewMode('personal');
    setSelectedMember(null);
    fetchData();
  };

  const handleTakeInventory = () => {
    setEditingSnapshot(null);
    setShowTakeModal(true);
  };

  const handleAddHistorical = () => {
    setEditingSnapshot(null);
    setShowTakeModal(true);
  };

  const handleEditSnapshot = (snapshot) => {
    setEditingSnapshot(snapshot);
    setShowTakeModal(true);
  };

  const handleDeleteSnapshot = async (snapshotId) => {
    if (!window.confirm('Are you sure you want to delete this inventory entry?')) {
      return;
    }

    try {
      const response = await fetch('/backend/api/inventory/delete-snapshot.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ snapshot_id: snapshotId })
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchData(); // Refresh data
      } else {
        alert('Failed to delete snapshot: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting snapshot:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleModalSave = (data) => {
    fetchData(); // Refresh data after save
  };

  const formatGrowth = (value) => {
    if (!value) return null;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}%`;
  };

  const getGrowthColor = (value) => {
    if (!value) return '#999';
    if (value > 0) return '#10b981';
    if (value < 0) return '#ef4444';
    return '#999';
  };

  const getGrowthIcon = (value) => {
    if (!value) return '→';
    if (value > 0) return '↑';
    if (value < 0) return '↓';
    return '→';
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getGrowthClass = (value) => {
    if (!value) return 'growth-neutral';
    if (value > 0) return 'growth-positive';
    if (value < 0) return 'growth-negative';
    return 'growth-neutral';
  };

  const handleInfoHover = (e, text) => {
    setShowTooltip(true);
    setTooltipData(text);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoLeave = () => {
    setShowTooltip(false);
    setTooltipData(null);
  };

  // Calculate Infinity Level Progress
  const calculateLevelProgress = () => {
    if (!currentSnapshot) return null;

    const levels = [3000, 15000, 45000, 100000, 200000, 350000, 550000, 1000000, 1800000, 3000000, 4500000, 6800000, 10000000];
    const currentTotal = currentSnapshot.team_infinity_points + currentSnapshot.personal_infinity_points;

    // Find next level
    const nextLevel = levels.find(level => level > currentTotal);
    if (!nextLevel) {
      return { pointsNeeded: 0, estimatedDate: 'MAX LEVEL REACHED' };
    }

    const pointsNeeded = nextLevel - currentTotal;

    // Calculate average MoM growth from historical data
    if (!allSnapshots || allSnapshots.length < 2) {
      return { pointsNeeded, estimatedDate: 'N/A' };
    }

    // Sort snapshots by date
    const sorted = [...allSnapshots].sort((a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date));
    
    let totalGrowth = 0;
    let growthCount = 0;

    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const prevTotal = prev.team_infinity_points + prev.personal_infinity_points;
      const currTotal = curr.team_infinity_points + curr.personal_infinity_points;
      const monthlyGrowth = currTotal - prevTotal;
      
      if (monthlyGrowth > 0) {
        totalGrowth += monthlyGrowth;
        growthCount++;
      }
    }

    if (growthCount === 0 || totalGrowth <= 0) {
      return { pointsNeeded, estimatedDate: 'N/A' };
    }

    const avgMonthlyGrowth = totalGrowth / growthCount;
    const monthsNeeded = pointsNeeded / avgMonthlyGrowth;
    const daysNeeded = Math.ceil(monthsNeeded * 30);

    const estimatedDate = new Date();
    estimatedDate.setDate(estimatedDate.getDate() + daysNeeded);

    const formattedDate = estimatedDate.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });

    return { pointsNeeded, estimatedDate: formattedDate };
  };

  const levelProgress = calculateLevelProgress();

  if (loading) {
    return (
      <div className="inventory-page">
        <div className="page-loading">Loading inventory data...</div>
      </div>
    );
  }

  return (
    <div className="inventory-page">
      {currentSnapshot ? (
        <>
          {/* Compact horizontal metrics - no wrapper box */}
          <div className="metrics-row">
            <div className="metric-compact">
              <div 
                className="metric-value"
                onMouseEnter={(e) => handleInfoHover(e, currentSnapshot.infinity_total.toLocaleString())}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
                style={{ cursor: 'help' }}
              >
                {formatNumber(currentSnapshot.infinity_total)}
              </div>
              <div className="metric-label">
                Infinity
                <span className="growth-badge positive">↑ {formatGrowth(growth?.infinity_total)}</span>
              </div>
            </div>
            
            <div className="metric-compact">
              <div 
                className="metric-value"
                onMouseEnter={(e) => handleInfoHover(e, currentSnapshot.monthly_total.toLocaleString())}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
                style={{ cursor: 'help' }}
              >
                {formatNumber(currentSnapshot.monthly_total)}
              </div>
              <div className="metric-label">
                Monthly
                <span className="growth-badge positive">↑ {formatGrowth(growth?.monthly_total)}</span>
              </div>
            </div>
            
            <div className="metric-compact">
              <div 
                className="metric-value"
                onMouseEnter={(e) => handleInfoHover(e, currentSnapshot.community_size.toLocaleString())}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
                style={{ cursor: 'help' }}
              >
                {formatNumber(currentSnapshot.community_size)}
              </div>
              <div className="metric-label">
                Community
                <span className="growth-badge positive">↑ {formatGrowth(growth?.community_size)}</span>
              </div>
            </div>
          </div>

          {/* Infinity Level Progress Boxes */}
          {levelProgress && (
            <div className="level-progress-row">
              <div className="metric-compact metric-wide">
                <div className="metric-value">
                  {levelProgress.pointsNeeded === 0 ? 'MAX' : formatNumber(levelProgress.pointsNeeded)}
                </div>
                <div className="metric-label">
                  POINTS NEEDED FOR NEXT INFINITY LEVEL
                </div>
              </div>
              
              <div className="metric-compact metric-wide">
                <div className="metric-value">
                  {levelProgress.estimatedDate}
                </div>
                <div className="metric-label">
                  NEXT LEVEL ESTIMATE DATE
                </div>
              </div>
            </div>
          )}

          {/* Compact growth comparison */}
          <div className="growth-stats-compact">
            <div className="growth-row">
              <span className="period-label">
                WoW
                <div 
                  className="info-icon-wrapper"
                  onMouseEnter={(e) => handleInfoHover(e, "Week-over-Week: Shows how much your metrics have grown compared to last week")}
                  onMouseMove={handleInfoMove}
                  onMouseLeave={handleInfoLeave}
                >
                  <img src={InfoIcon} alt="Info" className="info-icon" />
                </div>
              </span>
              <div className="growth-mini">
                <span className="label">Inf</span>
                <div className="bar-mini">
                  <div className="fill" style={{width: `${Math.min(Math.abs(growth?.infinity_total || 0), 100)}%`}}/>
                </div>
                <span className="pct">{formatGrowth(growth?.infinity_total)}</span>
              </div>
              <div className="growth-mini">
                <span className="label">Mon</span>
                <div className="bar-mini">
                  <div className="fill" style={{width: `${Math.min(Math.abs(growth?.monthly_total || 0), 100)}%`}}/>
                </div>
                <span className="pct">{formatGrowth(growth?.monthly_total)}</span>
              </div>
              <div className="growth-mini">
                <span className="label">Com</span>
                <div className="bar-mini">
                  <div className="fill" style={{width: `${Math.min(Math.abs(growth?.community_size || 0), 100)}%`}}/>
                </div>
                <span className="pct">{formatGrowth(growth?.community_size)}</span>
              </div>
            </div>
            
            <div className="growth-row">
              <span className="period-label">
                MoM
                <div 
                  className="info-icon-wrapper"
                  onMouseEnter={(e) => handleInfoHover(e, "Month-over-Month: Shows how much your metrics have grown compared to last month")}
                  onMouseMove={handleInfoMove}
                  onMouseLeave={handleInfoLeave}
                >
                  <img src={InfoIcon} alt="Info" className="info-icon" />
                </div>
              </span>
              <div className="growth-mini">
                <span className="label">Inf</span>
                <div className="bar-mini">
                  <div className="fill" style={{width: `${Math.min(Math.abs(monthlyGrowth?.infinity_total || 0), 100)}%`}}/>
                </div>
                <span className="pct">{formatGrowth(monthlyGrowth?.infinity_total)}</span>
              </div>
              <div className="growth-mini">
                <span className="label">Mon</span>
                <div className="bar-mini">
                  <div className="fill" style={{width: `${Math.min(Math.abs(monthlyGrowth?.monthly_total || 0), 100)}%`}}/>
                </div>
                <span className="pct">{formatGrowth(monthlyGrowth?.monthly_total)}</span>
              </div>
              <div className="growth-mini">
                <span className="label">Com</span>
                <div className="bar-mini">
                  <div className="fill" style={{width: `${Math.min(Math.abs(monthlyGrowth?.community_size || 0), 100)}%`}}/>
                </div>
                <span className="pct">{formatGrowth(monthlyGrowth?.community_size)}</span>
              </div>
            </div>
          </div>

          {/* Chart section */}
          <div className="chart-section">
            <div className="chart-header">
              <h3>Growth Trend</h3>
              <div className="chart-controls">
                <button 
                  className={selectedMetric === 'infinity' ? 'active' : ''} 
                  onClick={() => setSelectedMetric('infinity')}
                >
                  Infinity
                </button>
                <button 
                  className={selectedMetric === 'monthly' ? 'active' : ''} 
                  onClick={() => setSelectedMetric('monthly')}
                >
                  Monthly
                </button>
                <button 
                  className={selectedMetric === 'community' ? 'active' : ''} 
                  onClick={() => setSelectedMetric('community')}
                >
                  Community
                </button>
              </div>
            </div>
            {selectedMetric === 'monthly' ? (
              <BarChart 
                data={allSnapshots}
                metric={selectedMetric}
                height={250}
              />
            ) : (
              <LineChart 
                data={allSnapshots}
                metric={selectedMetric}
                height={250}
              />
            )}
          </div>
        </>
      ) : (
        <div className="no-data-card">
          <h2>No Inventory Data</h2>
          <p>Take your first inventory to start tracking your business growth.</p>
          <button className="btn-primary" onClick={handleTakeInventory}>
            Take Your First Inventory
          </button>
        </div>
      )}

      {/* Historical Entries */}
      {allSnapshots.length > 0 && (
        <div className="entries-section">
          <div className="section-header">
            <h3>Your Inputs <span className="count">({allSnapshots.length})</span></h3>
          </div>
          
          <div className="entries-table-compact">
            <div className="table-header-compact">
              <div>Date</div>
              <div>Infinity</div>
              <div>Monthly</div>
              <div>Community</div>
              <div>Δ</div>
              <div></div>
            </div>
            
            {allSnapshots.map((snapshot, index) => {
              const prevSnapshot = allSnapshots[index + 1];
              const wowGrowth = prevSnapshot ? 
                calculateGrowth(snapshot.infinity_total, prevSnapshot.infinity_total) : null;

              return (
                <div key={snapshot.id} className="table-row-compact">
                  <div className="cell-date">{formatDate(snapshot.snapshot_date)}</div>
                  <div className="cell-number">{formatNumber(snapshot.infinity_total)}</div>
                  <div className="cell-number">{formatNumber(snapshot.monthly_total)}</div>
                  <div className="cell-number">{formatNumber(snapshot.community_size)}</div>
                  <div className="cell-growth">
                    <span className={getGrowthClass(wowGrowth)}>
                      {formatGrowth(wowGrowth)}
                    </span>
                  </div>
                  <div className="cell-actions">
                    <button className="icon-btn edit-btn" onClick={() => handleEditSnapshot(snapshot)} title="Edit">
                      <img src={PenIcon} alt="Edit" />
                    </button>
                    <button className="icon-btn delete-btn" onClick={() => handleDeleteSnapshot(snapshot.id)} title="Delete">
                      <img src={TrashIcon} alt="Delete" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team Inventory Section - Only show in personal view */}
      {viewMode === 'personal' && (
        <TeamInventorySection 
          onMemberSelect={handleMemberSelect}
          selectedMemberId={selectedMember?.id}
        />
      )}

      {/* Take Inventory Modal */}
      {showTakeModal && (
        <TakeInventoryModal
          isOpen={showTakeModal}
          onClose={() => setShowTakeModal(false)}
          onSave={handleModalSave}
          editingSnapshot={editingSnapshot}
        />
      )}

      {/* Info Tooltip */}
      {showTooltip && tooltipData && (
        <div 
          className="info-tooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y
          }}
        >
          {tooltipData}
        </div>
      )}
    </div>
  );
}

function calculateGrowth(current, previous) {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return Math.round(((current - previous) / previous) * 100 * 10) / 10;
}

export default InventoryPage;
